import axiosInstance, { ApiKeys, getApiUrl } from '@cp/src/utils/ApiHelper';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { Dispatch } from '@reduxjs/toolkit';
import {
  setRecommendedCases,
  setRecommendedMetadata,
  appendRecommendedCases,
  setRecommendedCasesLoading,
  setCurrentRecommendedCase,
  setStrategyContextWithBanner
} from './reducer';
import {
  params,
  RecommendedCaseData,
  StrategyType,
  AllocationDpdFilter,
  V2StatsResponse,
  V2CaseRecommendationParams
} from './types';
import { API_STATUS_CODE } from '@cp/src/utils/ApiHelperUtils';
import { setCaseRecommendationStatsLoading } from '../CsaPerformanceDashboard/reducers/CsaPerformanceDashboardSlice';

export const fetchCaseRecommendationMetadata = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_STATS);

  axiosInstance
    .get(url, {
      headers: {
        donotHandleError: true,
        includeInUptimeMetrics: true
      }
    })
    .then(response => {
      if (response.data) {
        dispatch(setRecommendedMetadata(response.data));
      }
    })
    .catch(error => {
      toast.error('Error fetching case recommendation metadata');
    });
};

export const fetchCaseRecommendationCurrentCase =
  (successCallback: (currentRecommendedCase: RecommendedCaseData) => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_CURRENT_CASE);

    axiosInstance
      .get(url, {
        headers: {
          donotHandleError: true,
          includeInUptimeMetrics: true
        }
      })
      .then(response => {
        if (response.data) {
          successCallback(response.data);
        }
      })
      .catch(error => {
        toast.error('Error fetching case recommendation current case');
      });
  };

export const fetchCaseRecommendationNextCase =
  (successCallback: (currentRecommendedCase: RecommendedCaseData) => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_NEXT_CASE);

    axiosInstance
      .post(
        url,
        {},
        {
          headers: {
            donotHandleError: true,
            includeInUptimeMetrics: true
          }
        }
      )
      .then(response => {
        if (response.data) {
          successCallback(response.data);
        }
      })
      .catch(error => {
        toast.error('Error fetching case recommendation next case');
      });
  };

export const fetchCaseRecommendationCases =
  ({ limit, offset }: params) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_CASES);

    dispatch(setRecommendedCasesLoading(true));
    axiosInstance
      .get(url, {
        params: { limit, offset },
        headers: { donotHandleError: true, includeInUptimeMetrics: true }
      })
      .then(response => {
        if (response.data) {
          dispatch(setRecommendedCases(response.data));
        }
      })
      .catch(error => {
        toast.error('Error fetching case recommendation cases');
      })
      .finally(() => {
        dispatch(setRecommendedCasesLoading(false));
      });
  };

export const fetchMoreCaseRecommendationCases =
  ({ limit, offset }: params) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_CASES);

    axiosInstance
      .get(url, {
        params: { limit, offset },
        headers: { donotHandleError: true, includeInUptimeMetrics: true }
      })
      .then(response => {
        if (response.data) {
          dispatch(appendRecommendedCases(response.data));
        }
      })
      .catch(error => {
        toast.error('Error fetching more case recommendation cases');
      });
  };

export const fetchCaseRecommendationPreviousCase =
  (successCallback: (currentRecommendedCase: RecommendedCaseData) => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_PREVIOUS_CASE);

    axiosInstance
      .post(
        url,
        {},
        {
          headers: {
            donotHandleError: true,
            includeInUptimeMetrics: true
          }
        }
      )
      .then(response => {
        if (response.data) {
          successCallback(response.data);
        }
      })
      .catch(error => {
        toast.error('Error fetching case recommendation previous case');
      });
  };

export const fetchRefreshCaseRecommendation =
  ({ limit, offset, successCallback }: params) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_REFRESH);

    dispatch(setRecommendedCasesLoading(true));
    axiosInstance
      .post(
        url,
        {
          params: { limit, offset }
        },
        {
          headers: { donotHandleError: true, includeInUptimeMetrics: true }
        }
      )
      .then(response => {
        if (response.status === API_STATUS_CODE.OK) {
          successCallback?.();
        }
      })
      .catch(error => {
        toast.error('Error refreshing case recommendation');
      })
      .finally(() => {
        dispatch(setRecommendedCasesLoading(false));
      });
  };

export const fetchLatestCurrentCase =
  (successCallback: (currentRecommendedCase: RecommendedCaseData) => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_LATEST_CURRENT_CASE);

    axiosInstance
      .get(url, {
        headers: {
          donotHandleError: true
        }
      })
      .then(response => {
        if (response.data) {
          successCallback(response.data);
        }
      })
      .catch(error => {
        toast.error('Error fetching case recommendation current case');
      });
  };

export const fetchCaseRecommendationV2Stats =
  (allocationDpdFilter: AllocationDpdFilter, setStatsData: (stats: V2StatsResponse) => void) =>
  (dispatch: Dispatch) => {
    dispatch(setCaseRecommendationStatsLoading(true));
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_V2_STATS);

    axiosInstance
      .get(url, {
        params: { allocationDpdFilter },
        headers: {
          donotHandleError: true,
          includeInUptimeMetrics: true
        }
      })
      .then(response => {
        if (response.data) {
          setStatsData(response.data);
        }
      })
      .catch(() => {
        toast.error('Error fetching case recommendation stats');
      })
      .finally(() => {
        dispatch(setCaseRecommendationStatsLoading(false));
      });
  };

export const fetchCaseRecommendationV2CurrentCase =
  (params: V2CaseRecommendationParams) => (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_CURRENT_CASE_V2);

    axiosInstance
      .get(url, {
        params: {
          strategy: params.strategy,
          allocationDpdFilter: params.allocationDpdFilter
        },
        headers: {
          donotHandleError: true,
          includeInUptimeMetrics: true
        }
      })
      .then(response => {
        if (response.data) {
          dispatch(setCurrentRecommendedCase(response.data));
          dispatch(
            setStrategyContextWithBanner({
              strategyType: params.strategy,
              totalCasesInStrategy: response.data.totalCases,
              currentPositionInStrategy: response.data.priority,
              allocationDpdFilter: params.allocationDpdFilter
            })
          );
        }
      })
      .catch(() => {
        toast.error('Error fetching current case in strategy');
      });
  };

export const fetchCaseRecommendationV2NextCase =
  (
    params: V2CaseRecommendationParams,
    successCallback: (response: RecommendedCaseData) => void,
    onEndOfCases?: () => void
  ) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_V2_NEXT);

    axiosInstance
      .post(url, null, {
        params: {
          strategy: params.strategy,
          allocationDpdFilter: params.allocationDpdFilter
        },
        headers: {
          donotHandleError: true,
          includeInUptimeMetrics: true
        }
      })
      .then(response => {
        if (response.data) {
          successCallback(response.data);
        }
      })
      .catch(error => {
        if (error?.response?.status === API_STATUS_CODE.NOT_FOUND) {
          onEndOfCases?.();
          toast.error('No more cases in this strategy');
        } else {
          toast.error('Error fetching next case in strategy');
        }
      });
  };

export const fetchCaseRecommendationV2PreviousCase =
  (params: V2CaseRecommendationParams, successCallback: (response: RecommendedCaseData) => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.CASE_RECOMMENDATION_V2_PREVIOUS);

    axiosInstance
      .post(url, null, {
        params: {
          strategy: params.strategy,
          allocationDpdFilter: params.allocationDpdFilter
        },
        headers: {
          donotHandleError: true,
          includeInUptimeMetrics: true
        }
      })
      .then(response => {
        if (response.data) {
          successCallback(response.data);
        }
      })
      .catch(() => {
        toast.error('Error fetching previous case in strategy');
      });
  };
